import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { Users, RotateCcw, Trophy, X, Dice1, Dice2, Dice3, Dice4, Dice5, Dice6 } from "lucide-react";
import { toast } from "sonner";
import { AuthContext, WS_URL } from "../App";

const DICE_ICONS = [Dice1, Dice2, Dice3, Dice4, Dice5, Dice6];

// Dice face component
const DiceFace = ({ value, size = 'lg', rolling = false }) => {
  const Icon = DICE_ICONS[value - 1];
  const sizeClass = size === 'lg' ? 'w-16 h-16' : 'w-10 h-10';
  
  return (
    <div className={`${sizeClass} bg-white rounded-lg flex items-center justify-center shadow-lg ${rolling ? 'animate-bounce' : ''}`}>
      {Icon && <Icon className={`${size === 'lg' ? 'w-12 h-12' : 'w-8 h-8'} text-red-600`} />}
    </div>
  );
};

// Roll dice function
const rollDice = (count = 1) => {
  return Array.from({ length: count }, () => Math.floor(Math.random() * 6) + 1);
};

// ==================== LIAR'S DICE ====================
const LiarsDice = ({ onBack, players: initialPlayers, wsRef, currentTable }) => {
  const { user } = useContext(AuthContext);
  const [players, setPlayers] = useState(initialPlayers || []);
  const [myDice, setMyDice] = useState([]);
  const [currentBid, setCurrentBid] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [bidQuantity, setBidQuantity] = useState(1);
  const [bidFace, setBidFace] = useState(1);
  const [gamePhase, setGamePhase] = useState('waiting'); // waiting, playing, revealing
  const [revealed, setRevealed] = useState(false);
  const [allDice, setAllDice] = useState({});
  const [winner, setWinner] = useState(null);

  const startGame = () => {
    const dice = rollDice(5);
    setMyDice(dice);
    setGamePhase('playing');
    setCurrentBid(null);
    setCurrentPlayer(players[0]?.user_id);
  };

  const makeBid = () => {
    if (!currentBid || bidQuantity > currentBid.quantity || 
        (bidQuantity === currentBid.quantity && bidFace > currentBid.face)) {
      const newBid = { quantity: bidQuantity, face: bidFace, player: user?.username };
      setCurrentBid(newBid);
      // Move to next player
      const myIndex = players.findIndex(p => p.user_id === user?.user_id);
      const nextIndex = (myIndex + 1) % players.length;
      setCurrentPlayer(players[nextIndex]?.user_id);
    } else {
      toast.error("Bid must be higher!");
    }
  };

  const callLiar = () => {
    setGamePhase('revealing');
    setRevealed(true);
    // Count actual dice
    const allValues = [...myDice]; // In real game, combine all players' dice
    const actualCount = allValues.filter(d => d === currentBid?.face || d === 1).length; // 1s are wild
    
    if (actualCount < currentBid?.quantity) {
      toast.success("Correct! The bidder was lying!");
    } else {
      toast.error("Wrong! The bid was valid.");
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-yellow-400">🎲 LIAR'S DICE</h3>
        <button onClick={onBack} className="text-gray-400 hover:text-white text-sm">← Back</button>
      </div>

      {gamePhase === 'waiting' ? (
        <div className="text-center py-8">
          <p className="text-gray-400 mb-4">Bluff your way to victory!</p>
          <button
            onClick={startGame}
            className="bg-yellow-600 text-black px-8 py-3 rounded-lg font-bold hover:bg-yellow-500"
          >
            START GAME
          </button>
        </div>
      ) : (
        <>
          {/* My Dice */}
          <div className="bg-black/30 rounded-lg p-4 mb-4">
            <div className="text-sm text-gray-400 mb-2">Your dice (hidden from others):</div>
            <div className="flex gap-2 justify-center">
              {myDice.map((value, i) => (
                <DiceFace key={i} value={value} size="lg" />
              ))}
            </div>
          </div>

          {/* Current Bid */}
          {currentBid && (
            <div className="bg-purple-900/50 rounded-lg p-4 mb-4 text-center">
              <div className="text-sm text-gray-400 mb-1">Current Bid by {currentBid.player}:</div>
              <div className="text-2xl font-bold text-white">
                {currentBid.quantity} × <DiceFace value={currentBid.face} size="sm" />
              </div>
            </div>
          )}

          {/* Actions */}
          {currentPlayer === user?.user_id && (
            <div className="bg-black/30 rounded-lg p-4">
              <div className="text-sm text-gray-400 mb-2">Your turn - Make a bid or call liar!</div>
              <div className="flex items-center gap-4 mb-4 justify-center">
                <div>
                  <label className="text-xs text-gray-400">Quantity</label>
                  <select 
                    value={bidQuantity}
                    onChange={(e) => setBidQuantity(parseInt(e.target.value))}
                    className="block w-20 bg-gray-700 text-white rounded px-2 py-1"
                  >
                    {[1,2,3,4,5,6,7,8,9,10].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-400">Face</label>
                  <select 
                    value={bidFace}
                    onChange={(e) => setBidFace(parseInt(e.target.value))}
                    className="block w-20 bg-gray-700 text-white rounded px-2 py-1"
                  >
                    {[1,2,3,4,5,6].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={makeBid}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-green-500"
                >
                  BID
                </button>
                {currentBid && (
                  <button
                    onClick={callLiar}
                    className="bg-red-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-red-500"
                  >
                    CALL LIAR!
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ==================== CRAPS ====================
const CrapsGame = ({ onBack }) => {
  const { user } = useContext(AuthContext);
  const [dice, setDice] = useState([1, 1]);
  const [rolling, setRolling] = useState(false);
  const [point, setPoint] = useState(null);
  const [phase, setPhase] = useState('comeOut'); // comeOut, point
  const [bet, setBet] = useState(10);
  const [balance, setBalance] = useState(user?.fchips_balance || 1000);
  const [message, setMessage] = useState("Place your bet and roll!");
  const [betType, setBetType] = useState('pass'); // pass, dontPass

  const roll = () => {
    if (bet > balance) {
      toast.error("Insufficient balance");
      return;
    }

    setRolling(true);
    
    // Animate dice
    const rollInterval = setInterval(() => {
      setDice(rollDice(2));
    }, 100);

    setTimeout(() => {
      clearInterval(rollInterval);
      const finalDice = rollDice(2);
      setDice(finalDice);
      setRolling(false);
      
      const total = finalDice[0] + finalDice[1];
      
      if (phase === 'comeOut') {
        // Come-out roll
        if (total === 7 || total === 11) {
          // Natural - pass wins
          if (betType === 'pass') {
            setBalance(prev => prev + bet);
            setMessage(`🎉 Natural ${total}! You win ${bet}!`);
          } else {
            setBalance(prev => prev - bet);
            setMessage(`Natural ${total}. Don't Pass loses.`);
          }
        } else if (total === 2 || total === 3 || total === 12) {
          // Craps - pass loses
          if (betType === 'pass') {
            setBalance(prev => prev - bet);
            setMessage(`Craps ${total}! You lose.`);
          } else {
            if (total === 12) {
              setMessage(`Push on 12. Bet returned.`);
            } else {
              setBalance(prev => prev + bet);
              setMessage(`🎉 Craps ${total}! Don't Pass wins!`);
            }
          }
        } else {
          // Point established
          setPoint(total);
          setPhase('point');
          setMessage(`Point is ${total}. Roll again to hit the point!`);
        }
      } else {
        // Point phase
        if (total === point) {
          // Hit the point - pass wins
          if (betType === 'pass') {
            setBalance(prev => prev + bet);
            setMessage(`🎉 Point ${point}! You win ${bet}!`);
          } else {
            setBalance(prev => prev - bet);
            setMessage(`Point ${point} hit. Don't Pass loses.`);
          }
          setPhase('comeOut');
          setPoint(null);
        } else if (total === 7) {
          // Seven out - pass loses
          if (betType === 'pass') {
            setBalance(prev => prev - bet);
            setMessage(`Seven out! You lose.`);
          } else {
            setBalance(prev => prev + bet);
            setMessage(`🎉 Seven out! Don't Pass wins!`);
          }
          setPhase('comeOut');
          setPoint(null);
        } else {
          setMessage(`Rolled ${total}. Keep rolling for ${point}!`);
        }
      }
    }, 1000);
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-green-400">🎲 CRAPS</h3>
        <button onClick={onBack} className="text-gray-400 hover:text-white text-sm">← Back</button>
      </div>

      {/* Balance */}
      <div className="bg-black/30 rounded-lg p-3 mb-4 flex items-center justify-between">
        <span className="text-gray-400">Balance:</span>
        <span className="text-yellow-400 font-bold">{balance} F-Chips</span>
      </div>

      {/* Dice */}
      <div className="flex justify-center gap-4 mb-4">
        {dice.map((value, i) => (
          <DiceFace key={i} value={value} size="lg" rolling={rolling} />
        ))}
      </div>

      {/* Message */}
      <div className="bg-green-900/50 rounded-lg p-3 mb-4 text-center">
        <p className="text-white font-bold">{message}</p>
        {point && <p className="text-yellow-400 text-sm mt-1">Point: {point}</p>}
      </div>

      {/* Betting */}
      <div className="bg-black/30 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-center gap-4 mb-4">
          <button
            onClick={() => setBetType('pass')}
            className={`px-4 py-2 rounded font-bold ${betType === 'pass' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300'}`}
          >
            PASS LINE
          </button>
          <button
            onClick={() => setBetType('dontPass')}
            className={`px-4 py-2 rounded font-bold ${betType === 'dontPass' ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-300'}`}
          >
            DON'T PASS
          </button>
        </div>
        <div className="flex items-center justify-center gap-2 mb-4">
          <span className="text-gray-400">Bet:</span>
          {[10, 25, 50, 100].map(amt => (
            <button
              key={amt}
              onClick={() => setBet(amt)}
              className={`px-3 py-1 rounded ${bet === amt ? 'bg-yellow-500 text-black' : 'bg-gray-700 text-white'} font-bold`}
            >
              {amt}
            </button>
          ))}
        </div>
      </div>

      {/* Roll Button */}
      <button
        onClick={roll}
        disabled={rolling}
        className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-4 rounded-lg font-bold text-xl hover:from-red-500 hover:to-red-600 disabled:opacity-50"
      >
        {rolling ? '🎲 ROLLING...' : '🎲 ROLL DICE'}
      </button>
    </div>
  );
};

// ==================== 13 ACES ====================
const ThirteenAces = ({ onBack }) => {
  const { user } = useContext(AuthContext);
  const [dice, setDice] = useState([1, 1, 1, 1, 1]);
  const [rolling, setRolling] = useState(false);
  const [keptDice, setKeptDice] = useState([false, false, false, false, false]);
  const [rollsLeft, setRollsLeft] = useState(3);
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(1);
  const [totalScore, setTotalScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [message, setMessage] = useState("Roll to start!");
  const MAX_ROUNDS = 13;

  const roll = () => {
    if (rollsLeft <= 0) return;
    
    setRolling(true);
    
    // Animate dice
    const rollInterval = setInterval(() => {
      setDice(prev => prev.map((d, i) => keptDice[i] ? d : Math.floor(Math.random() * 6) + 1));
    }, 100);

    setTimeout(() => {
      clearInterval(rollInterval);
      const finalDice = dice.map((d, i) => keptDice[i] ? d : Math.floor(Math.random() * 6) + 1);
      setDice(finalDice);
      setRolling(false);
      setRollsLeft(prev => prev - 1);
      
      // Calculate score (count 1s as "aces")
      const aces = finalDice.filter(d => d === 1).length;
      const sum = finalDice.reduce((a, b) => a + b, 0);
      
      if (aces >= 1) {
        setScore(aces * 10 + sum);
        setMessage(`${aces} Ace(s)! Score: ${aces * 10 + sum}`);
      } else {
        setScore(sum);
        setMessage(`No aces. Sum: ${sum}`);
      }
    }, 800);
  };

  const toggleKeep = (index) => {
    if (rollsLeft === 3) return; // Can't keep before first roll
    setKeptDice(prev => {
      const newKept = [...prev];
      newKept[index] = !newKept[index];
      return newKept;
    });
  };

  const endTurn = () => {
    setTotalScore(prev => prev + score);
    if (round >= MAX_ROUNDS) {
      setGameOver(true);
      setMessage(`Game Over! Final Score: ${totalScore + score}`);
    } else {
      setRound(prev => prev + 1);
      setDice([1, 1, 1, 1, 1]);
      setKeptDice([false, false, false, false, false]);
      setRollsLeft(3);
      setScore(0);
      setMessage("Roll to start!");
    }
  };

  const newGame = () => {
    setDice([1, 1, 1, 1, 1]);
    setKeptDice([false, false, false, false, false]);
    setRollsLeft(3);
    setScore(0);
    setRound(1);
    setTotalScore(0);
    setGameOver(false);
    setMessage("Roll to start!");
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-purple-400">🃏 13 ACES</h3>
        <button onClick={onBack} className="text-gray-400 hover:text-white text-sm">← Back</button>
      </div>

      {/* Score Display */}
      <div className="bg-black/30 rounded-lg p-3 mb-4 flex items-center justify-between">
        <span className="text-gray-400">Round: {round}/{MAX_ROUNDS}</span>
        <span className="text-purple-400 font-bold">Total: {totalScore}</span>
        <span className="text-yellow-400">This turn: {score}</span>
      </div>

      {/* Dice */}
      <div className="flex justify-center gap-3 mb-4">
        {dice.map((value, i) => (
          <button
            key={i}
            onClick={() => toggleKeep(i)}
            disabled={rollsLeft === 3}
            className={`relative ${keptDice[i] ? 'ring-4 ring-green-400' : ''}`}
          >
            <DiceFace value={value} size="lg" rolling={rolling && !keptDice[i]} />
            {keptDice[i] && (
              <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-1 rounded">
                KEEP
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Message */}
      <div className="bg-purple-900/50 rounded-lg p-3 mb-4 text-center">
        <p className="text-white font-bold">{message}</p>
        <p className="text-gray-400 text-sm mt-1">Rolls left: {rollsLeft}</p>
      </div>

      {/* Actions */}
      {!gameOver ? (
        <div className="flex gap-4 justify-center">
          <button
            onClick={roll}
            disabled={rolling || rollsLeft <= 0}
            className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-8 py-3 rounded-lg font-bold hover:from-purple-500 hover:to-purple-600 disabled:opacity-50"
          >
            {rolling ? '🎲 ROLLING...' : `🎲 ROLL (${rollsLeft})`}
          </button>
          {rollsLeft < 3 && (
            <button
              onClick={endTurn}
              className="bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-500"
            >
              ✓ BANK SCORE
            </button>
          )}
        </div>
      ) : (
        <div className="text-center">
          <p className="text-2xl font-bold text-purple-400 mb-4">Final Score: {totalScore}</p>
          <button
            onClick={newGame}
            className="bg-purple-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-purple-500"
          >
            🔄 PLAY AGAIN
          </button>
        </div>
      )}

      {/* Rules */}
      <div className="mt-4 bg-black/20 rounded p-3 text-xs text-gray-400">
        <p><strong>How to play:</strong> Roll 5 dice up to 3 times per round. Click dice to keep them between rolls. Each ACE (1) is worth 10 bonus points. Bank your score to move to the next round.</p>
      </div>
    </div>
  );
};

// ==================== MAIN DICE GAMES COMPONENT ====================
const DiceGames = ({ onClose }) => {
  const { token, user } = useContext(AuthContext);
  const [view, setView] = useState('menu'); // menu, liars, craps
  const [currentTable, setCurrentTable] = useState(null);
  const [players, setPlayers] = useState([]);
  const wsRef = useRef(null);

  const connectWs = useCallback((tableCode, game) => {
    if (wsRef.current) wsRef.current.close();
    
    const ws = new WebSocket(`${WS_URL}/ws/dice/${tableCode}?token=${token}&game=${game}`);
    
    ws.onopen = () => {
      console.log("Dice WS connected");
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'game_state') {
        setPlayers(data.players || []);
      }
    };
    
    wsRef.current = ws;
  }, [token]);

  const createGame = (game) => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    setCurrentTable({ code, game });
    connectWs(code, game);
    setPlayers([{ user_id: user?.user_id, username: user?.username }]);
    setView(game);
  };

  const joinGame = (code, game) => {
    setCurrentTable({ code, game });
    connectWs(code, game);
    setView(game);
  };

  useEffect(() => {
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  if (view === 'menu') {
    return (
      <div className="w-full h-full bg-gradient-to-b from-red-900 to-red-950 p-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              🎲 DICE GAMES
            </h2>
            <button onClick={onClose} className="text-white hover:text-red-400">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {/* Liar's Dice */}
            <div className="bg-black/30 rounded-lg p-4">
              <h3 className="text-xl font-bold text-yellow-400 mb-2">🎭 LIAR'S DICE</h3>
              <p className="text-gray-400 text-sm mb-3">Bluff your opponents! Bid on dice across all players, or call their bluff.</p>
              <button
                onClick={() => createGame('liars')}
                className="bg-yellow-600 text-black px-6 py-2 rounded-lg font-bold hover:bg-yellow-500"
              >
                PLAY
              </button>
            </div>

            {/* Craps */}
            <div className="bg-black/30 rounded-lg p-4">
              <h3 className="text-xl font-bold text-green-400 mb-2">🎰 CRAPS</h3>
              <p className="text-gray-400 text-sm mb-3">Classic casino dice game! Roll 7 or 11 to win, avoid crapping out.</p>
              <button
                onClick={() => createGame('craps')}
                className="bg-green-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-green-500"
              >
                PLAY
              </button>
            </div>

            {/* 13 Aces */}
            <div className="bg-black/30 rounded-lg p-4">
              <h3 className="text-xl font-bold text-purple-400 mb-2">🃏 13 ACES</h3>
              <p className="text-gray-400 text-sm mb-3">Roll dice and try to get the highest score without busting!</p>
              <button
                onClick={() => createGame('aces')}
                className="bg-purple-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-purple-500"
              >
                PLAY
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'liars') {
    return (
      <div className="w-full h-full bg-gradient-to-b from-yellow-900 to-orange-950">
        <div className="bg-black/40 px-4 py-2 flex items-center justify-between">
          <span className="text-white font-bold">Game: {currentTable?.code}</span>
          <span className="text-gray-400 flex items-center gap-1">
            <Users className="w-4 h-4" /> {players.length}
          </span>
        </div>
        <LiarsDice onBack={() => setView('menu')} players={players} wsRef={wsRef} currentTable={currentTable} />
      </div>
    );
  }

  if (view === 'craps') {
    return (
      <div className="w-full h-full bg-gradient-to-b from-green-900 to-green-950">
        <div className="bg-black/40 px-4 py-2">
          <span className="text-white font-bold">CRAPS</span>
        </div>
        <CrapsGame onBack={() => setView('menu')} />
      </div>
    );
  }

  if (view === 'aces') {
    return (
      <div className="w-full h-full bg-gradient-to-b from-purple-900 to-indigo-950">
        <div className="bg-black/40 px-4 py-2 flex items-center justify-between">
          <span className="text-white font-bold">13 ACES</span>
          <span className="text-gray-400 flex items-center gap-1">
            <Users className="w-4 h-4" /> {players.length}
          </span>
        </div>
        <ThirteenAces onBack={() => setView('menu')} />
      </div>
    );
  }

  return null;
};

export default DiceGames;
